import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Flex, Box } from 'reflexbox';
import {
  Message,
  Panel,
  PanelHeader,
  Text,
} from 'rebass';
import { FixedSizeList as List } from 'react-window';

import {
  If,
  TextDivider,
  WowIcon,
} from 'frontend/components';

import config from 'frontend/config';

const timestamp = totalSeconds => {
  const minutes = _.floor(totalSeconds / 60);
  const seconds = _.floor(totalSeconds) % 60;
  const ms = _.round(1000 * (totalSeconds - _.floor(totalSeconds)));
  return `${minutes || 0}:${_.padStart(seconds, 2, '0')}:${_.padStart(ms, 3, '0')}`;
};

const ignoreActions = [
  'auto_attack',
];

const shouldIgnoreAction = action => action.queue_failed || _.includes(ignoreActions, action.name);

const ROW_HEIGHT = 36;

const styles = {
  default: {
    lineHeight: 1,
  },
  stack: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    background: 'rgba(0, 0, 0, 0.9)',
    padding: '1px 0px 0px 1px',
    margin: 0,
    lineHeight: 1,
    pointerEvents: 'none',
  },
};

const ActionRow = ({ action }) => {
  return (
    <Flex align="center" style={{ height: ROW_HEIGHT }}>
      <Box col={1}>
        <Text small style={styles.default} color={config.colors.midgray}>
          {timestamp(action.time)}
        </Text>
      </Box>
      <Box col={3}>
        <Flex align="center">
          <If
            condition={action.id}
            then={() => (
              <Box
                style={{ height: 26 }}
                mr={1}
              >
                <WowIcon
                  type="spell"
                  id={action.id}
                  size={26}
                  border="1px solid #ffffff33"
                />
              </Box>
            )}
          />
          <Text size={13} style={styles.default}>
            <If
              condition={action.wait}
              then={() => 'Wait for Resource'}
              else={() => action.spell_name || _.startCase(action.name)}
            />
          </Text>
        </Flex>
      </Box>
      <Box col={2} mr={1}>
        <Text small style={styles.default}>
          {action.target !== 'none' ? _.replace(action.target, /_/g, ' ') : ''}
        </Text>
      </Box>
      <Box col={4}>
        <Flex gutter={1}>
          {_.map(_.filter(action.buffs, 'id'), buff => (
            <Box
              key={`${buff.id}-${buff.name}`}
              ml={1}
              style={{
                position: 'relative',
                height: 20,
              }}
            >
              <WowIcon
                id={buff.id}
                type="spell"
                size={20}
                border="1px solid #ffffff33"
              />
              <If
                condition={buff.stacks > 1}
                then={() => (
                  <Text
                    small
                    style={styles.stack}
                  >
                    {buff.stacks}
                  </Text>
                )}
              />
            </Box>
          ))}
        </Flex>
      </Box>
    </Flex>
  );
};

ActionRow.propTypes = {
  action: PropTypes.object.isRequired,
};

const ActionSequence = ({ sequence, precombat }) => {
  const wrapRow = action => ({ type: 'row', action });
  const rows = [
    {
      type: 'divider',
      text: 'Pre-Pull',
    },
    ..._.map(precombat, wrapRow),
    {
      type: 'divider',
      text: 'Pull',
    },
    ..._.map(_.reject(sequence, shouldIgnoreAction), wrapRow),
  ];
  return (
    <Panel theme="primary" pt={2} pb={1} mr={0}>
      <PanelHeader p={1} mb={1}>
        Sample Ability Log
      </PanelHeader>
      <Message theme="warning" p={1}>
        This is a sample of 1 iteration, it is not a guide on how to play your character. Analyze with care.
      </Message>
      <Text small mb={1}>
        This log provides a sample sequence of actions used in one iteration and will vary between sims based on
        RNG procs/buffs
      </Text>
      <List
        height={400}
        itemCount={rows.length}
        itemSize={ROW_HEIGHT}
        width={824}
        overscanCount={10}
      >
        {({ index, style }) => (
          <div style={style}>
            {
              rows[index].type === 'row'
                ? <ActionRow action={rows[index].action} />
                : <TextDivider text={rows[index].text} left={false} />
            }
          </div>
        )}
      </List>
      {/*
      <Box
        style={{
          height: 400,
          overflowY: 'scroll',
          WebkitOverflowScrolling: 'touch',
          marginRight: -14,
        }}
      >
        <Text small>
          This log provides a sample sequence of actions used in one iteration and will vary between sims based on
          RNG procs/buffs
        </Text>
        <If
          condition={_.size(precombat) > 0}
          then={() => (
            <Box>
              <TextDivider text="Pre-Pull" left={false} />
              {_.map(precombat, action => (
                <ActionRow key={`${action.time}-${action.name}`} action={action} />
              ))}
            </Box>
          )}
        />
        <TextDivider text="Pull" left={false} />
        {_.map(_.reject(sequence, shouldIgnoreAction), action => (
          <ActionRow key={`${action.time}-${action.name}`} action={action} />
        ))}
      </Box>
      */}
    </Panel>
  );
};

ActionSequence.propTypes = {
  sequence: PropTypes.array.isRequired,
  precombat: PropTypes.array.isRequired,
};

export default ActionSequence;
